import React, { useEffect, useId, useRef, useState } from 'react';
import { motion } from 'framer-motion';
// Identificación sin referencias al cuerpo como medida (cintura, ropa): describe lo que la persona
// nota y cómo se siente, en línea con el lenguaje del resto del producto.
const antes = [{
  bold: 'Te levantás y sentís que necesitás recuperar energía,',
  light: 'aunque hayas dormido las horas habituales.',
  icon: 'battery'
}, {
  bold: 'Notás cambios en tu cuerpo y en cómo te sentís,',
  light: 'aunque sigas haciendo muchas cosas como antes.',
  icon: 'shift'
}, {
  bold: 'Llegás a la tarde y necesitás hacer una pausa,',
  light: 'tomar un café o buscar algo dulce para seguir con el día.',
  icon: 'clock'
}];

// Lenguaje prudente: describe lo que la persona tiene al terminar (plan, registro, hábitos, claridad),
// no resultados fisiológicos ni cambios en el cuerpo.
const despues = [{
  bold: 'Un plan claro',
  light: 'para tu comida, tu movimiento y tu descanso.',
  icon: 'sun'
}, {
  bold: 'Un registro propio',
  light: 'de cómo te sentís, para mirarlo a tu ritmo.',
  icon: 'check'
}, {
  bold: 'Hábitos que podés sostener',
  light: 'más allá de los 21 días.',
  icon: 'steady'
}, {
  bold: 'Más claridad',
  light: 'sobre qué hacer con tus rutinas.',
  icon: 'bulb'
}];
type IconName = 'battery' | 'shift' | 'clock' | 'tangle' | 'sun' | 'check' | 'steady' | 'bulb';
function Icon({
  name,
  className
}: {
  name: IconName;
  className?: string;
}) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.3,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
    'aria-hidden': true
  };
  switch (name) {
    case 'battery':
      return <svg {...common} data-id="element-42" data-mp="3chet0i7nez8d"><rect x="2" y="7" width="17" height="10" rx="2" data-id="element-43" data-mp="3ck0wa5niasks" /><path d="M21 10.5v3" data-id="element-44" data-mp="3hhsol0s2cgpi" /><rect x="4.2" y="9.2" width="3.4" height="5.6" rx="0.6" fill="currentColor" stroke="none" data-id="element-45" data-mp="3hic4yd22ex1f" /></svg>;
    case 'shift':
      return <svg {...common} data-id="element-46" data-mp="3mdzyf4nkyccq"><path d="M4 8h12M13 5l3 3-3 3" data-id="element-47" data-mp="1mduotozc4qpb" /><path d="M20 16H8M11 13l-3 3 3 3" data-id="element-48" data-mp="1md35pdmxclfx" /></svg>;
    case 'clock':
      return <svg {...common} data-id="element-49" data-mp="1hf1jwky4680r"><circle cx="12" cy="12" r="8.3" data-id="element-50" data-mp="1hf9vm9ii37ad" /><path d="M12 7.6V12l3 2" data-id="element-51" data-mp="1hgcnokrk7frv" /></svg>;
    case 'tangle':
      return <svg {...common} data-id="element-52" data-mp="1w3xq0cgm1ayq"><path d="M3 12c2-4 4 4 6 0s4-4 6 0 4 4 6 0" data-id="element-53" data-mp="1w4pk1iuk70mw" /></svg>;
    case 'sun':
      return <svg {...common} data-id="element-54" data-mp="1rco2bhemghph"><circle cx="12" cy="12" r="4.3" data-id="element-55" data-mp="1rbwg2x6wzwbv" /><path d="M12 3v2.3M12 18.7V21M4.2 12H2M22 12h-2.2M6.3 6.3 7.8 7.8M16.2 16.2l1.5 1.5M17.7 6.3 16.2 7.8M7.8 16.2l-1.5 1.5" data-id="element-56" data-mp="1rauu3xod1pfd" /></svg>;
    case 'check':
      return <svg {...common} data-id="element-57" data-mp="211phx6h98x2x"><circle cx="12" cy="12" r="8.3" data-id="element-58" data-mp="2127lypa5sfle" /><path d="M8.3 12.2l2.5 2.5 4.9-5.4" data-id="element-59" data-mp="2153iqaa9sy89" /></svg>;
    case 'steady':
      return <svg {...common} data-id="element-60" data-mp="2f1qsxer8hkw4"><path d="M3 12h5.5" data-id="element-61" data-mp="2f0fmt8abg11u" /><circle cx="12" cy="12" r="2.2" data-id="element-62" data-mp="2a3oyjxocxz5c" /><path d="M15.5 12H21" data-id="element-63" data-mp="2a4gxnih5yf0d" /></svg>;
    case 'bulb':
      return <svg {...common} data-id="element-64" data-mp="kohi9jmybtca"><path d="M9.3 18h5.4M10.3 21h3.4" data-id="element-65" data-mp="knzcnweeg2rp" /><path d="M12 3.2a5.8 5.8 0 0 0-3.1 10.7c.5.34.7.9.7 1.4v.4h4.8v-.4c0-.5.2-1.06.7-1.4A5.8 5.8 0 0 0 12 3.2Z" data-id="element-66" data-mp="kp0zey4s2dp9" /></svg>;
  }
}

// Ritmo de lectura holgado para 4 puntos breves antes de rotar automáticamente al otro estado.
const AUTO_ADVANCE_MS = 5200;
// Tras interactuar (tocar, pasar el mouse, enfocar), la rotación retoma a los ~1.8s — mismo patrón que el carrusel del Hero.
const RESUME_DELAY_MS = 1800;
type DiffState = 'antes' | 'despues';
const LABEL_ANTES = 'Tu punto de partida';
const LABEL_DESPUES = 'Lo que te llevás';
export function DifferenceSection() {
  const uid = useId();
  const [active, setActive] = useState<DiffState>('antes');
  const [isPlaying, setIsPlaying] = useState(true);
  const isPlayingRef = useRef(isPlaying);
  const isPausedRef = useRef(false);
  const isInViewRef = useRef(false);
  const prefersReducedMotionRef = useRef(false);
  const tickTimeoutRef = useRef<number | null>(null);
  const resumeTimeoutRef = useRef<number | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  useEffect(() => {
    prefersReducedMotionRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const node = cardRef.current;
    let observer: IntersectionObserver | null = null;
    if (node) {
      observer = new IntersectionObserver(([entry]) => {
        isInViewRef.current = entry.isIntersecting;
      }, {
        threshold: 0.4
      });
      observer.observe(node);
    }
    const scheduleTick = () => {
      tickTimeoutRef.current = window.setTimeout(() => {
        if (isPlayingRef.current && !isPausedRef.current && isInViewRef.current) {
          setActive(prev => prev === 'antes' ? 'despues' : 'antes');
        }
        scheduleTick();
      }, AUTO_ADVANCE_MS);
    };
    scheduleTick();
    return () => {
      if (tickTimeoutRef.current !== null) window.clearTimeout(tickTimeoutRef.current);
      if (resumeTimeoutRef.current !== null) window.clearTimeout(resumeTimeoutRef.current);
      observer?.disconnect();
    };
  }, []);
  const pauseTemporarily = () => {
    isPausedRef.current = true;
    if (resumeTimeoutRef.current !== null) {
      window.clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  };
  const scheduleResume = () => {
    if (resumeTimeoutRef.current !== null) window.clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = window.setTimeout(() => {
      isPausedRef.current = false;
      resumeTimeoutRef.current = null;
    }, RESUME_DELAY_MS);
  };
  const selectState = (state: DiffState) => {
    setActive(state);
    pauseTemporarily();
    scheduleResume();
  };
  const togglePlaying = () => {
    setIsPlaying(prev => !prev);
  };
  const antesPanelId = `${uid}-panel-antes`;
  const despuesPanelId = `${uid}-panel-despues`;
  const renderList = (items: typeof antes, tone: 'antes' | 'despues') => <ul className="space-y-4" data-id="element-67" data-mp="3bv01pekc5ppw">
      {items.map((item, i) => <li key={i} className="flex gap-3" data-id="element-68" data-mp="3bugqsht3s1ld" data-mpi={i}>
          <Icon name={item.icon as IconName} className={`w-[18px] h-[18px] flex-shrink-0 mt-0.5 ${tone === 'antes' ? 'text-[#C8A870]/45' : 'text-[#C8A870]'}`} data-id="element-69" data-mp="3gtte13nvzaoh" />
          <p className={`font-body leading-[1.45] ${tone === 'antes' ? 'text-[#F0E8D8]/70' : 'text-[#F0E8D8]/85'}`} style={{
        fontSize: '14px'
      }} data-id="element-70" data-mp="38rkf57xk8fsx">
            <strong className={`font-semibold ${tone === 'antes' ? 'text-[#F0E8D8]/80' : 'text-[#F0E8D8]'}`} data-id="element-71" data-mp="3ijgesnfaodz9">{item.bold}</strong> {item.light}
          </p>
        </li>)}
    </ul>;
  return <section className="bg-gradient-section py-24 md:py-32 px-5 md:px-8" style={{
    paddingBottom: '0px',
    paddingTop: '15px'
  }} data-id="element-72" data-mp="2pu9djgoghaab">
      <div ref={cardRef} className="champagne-frame max-w-2xl mx-auto rounded-[6px] px-5 py-10 md:px-10 md:py-12" style={{
      border: '0.5px solid rgba(200,168,112,0.14)',
      paddingBottom: "0px"
    }} data-id="element-73" data-mp="2kwq02izl11jf">
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center uppercase tracking-[0.28em] text-[#C8A870] mb-5" style={{
        fontSize: '13px',
        fontFamily: 'sans-serif',
        fontWeight: 600
      }} data-id="element-74" data-mp="2ust3rta3188h">
          Tu recorrido de 21 días
        </motion.p>

        <motion.h2 initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        delay: 0.1
      }} className="text-center font-display font-semibold text-[#F0E8D8] leading-[1.05] mb-8 md:mb-10" style={{
        fontSize: 'clamp(34px, 7vw, 58px)'
      }} data-id="element-75" data-mp="mlpi1ldgmc37">
          De la improvisación{' '}
          <span className="italic text-gradient-gold" data-id="element-76" data-mp="10fqda557dgyt">a una estrategia.</span>
        </motion.h2>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.15
      }} data-id="element-77" data-mp="15ebb5xwzfok3">
          {/* Ancla de tiempo: reemplaza el círculo flanqueado por líneas verticales de la versión anterior por un divisor horizontal compacto, para ganar altura. */}
          <div className="flex items-center justify-center gap-3 mb-7" data-id="element-78" data-mp="1o3rndii155gc">
            <span className="h-px w-10 md:w-14" style={{
            background: 'linear-gradient(to right, transparent, rgba(200,168,112,0.4))'
          }} data-id="element-79" data-mp="zckd0oyty2fg" />
            <span className="uppercase tracking-[0.24em] text-[#C8A870]" style={{
            fontSize: '12px',
            fontFamily: 'sans-serif',
            fontWeight: 700
          }} data-id="element-80" data-mp="ujixceqstyut">
              21 días
            </span>
            <span className="h-px w-10 md:w-14" style={{
            background: 'linear-gradient(to left, transparent, rgba(200,168,112,0.4))'
          }} data-id="element-81" data-mp="1ib6ei4y9dvjw" />
          </div>

          {/* Selector: son exactamente 2 controles para 2 estados reales (no hay indicadores adicionales). Funcionan como botones de alternancia (aria-pressed), accesibles por teclado por ser <button> nativos. Con flex-wrap para que las etiquetas más largas no desborden en pantallas angostas. */}
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3 mb-8" data-id="element-82" data-mp="3t4j17oj2fbjy">
            <button type="button" onClick={() => selectState('antes')} aria-pressed={active === 'antes'} aria-controls={antesPanelId} className="uppercase tracking-[0.14em] pb-2 transition-colors duration-300" style={{
            fontSize: '12px',
            fontFamily: 'sans-serif',
            fontWeight: 600,
            color: active === 'antes' ? '#F0E8D8' : 'rgba(240,232,216,0.3)',
            borderBottom: active === 'antes' ? '1px solid rgba(200,168,112,0.5)' : '0.5px solid rgba(240,232,216,0.16)'
          }} data-id="element-83" data-mp="3o2lbqck2nkik">
              {LABEL_ANTES}
            </button>

            <button type="button" onClick={() => selectState('despues')} aria-pressed={active === 'despues'} aria-controls={despuesPanelId} className="uppercase tracking-[0.14em] pb-2 transition-colors duration-300" style={{
            fontSize: '12px',
            fontFamily: 'sans-serif',
            fontWeight: 600,
            color: active === 'despues' ? '#C8A870' : 'rgba(240,232,216,0.3)',
            borderBottom: active === 'despues' ? '1px solid rgba(200,168,112,0.5)' : '0.5px solid rgba(240,232,216,0.16)'
          }} data-id="element-84" data-mp="2rbew35nf5oxq">
              {LABEL_DESPUES}
            </button>

            {/* Control de pausa: requisito de accesibilidad (WCAG 2.2.2) para todo contenido que se mueve o cambia automáticamente por más de 5s — detiene la rotación de forma persistente, no solo temporal. */}
            <button type="button" onClick={togglePlaying} aria-pressed={!isPlaying} aria-label={isPlaying ? 'Pausar transición automática' : 'Reanudar transición automática'} className="flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0" style={{
            border: '1px solid rgba(200,168,112,0.3)',
            color: '#C8A870'
          }} data-id="element-85" data-mp="14kwyhsqnrlq9">
              {isPlaying ? <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor" aria-hidden="true" data-id="element-86" data-mp="1n9k36a4xa93b"><rect x="5" y="4" width="4.5" height="16" rx="1" data-id="element-87" data-mp="1s73izeh9qtxd" /><rect x="14.5" y="4" width="4.5" height="16" rx="1" data-id="element-88" data-mp="1s9y3t7472grb" /></svg> : <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor" aria-hidden="true" data-id="element-89" data-mp="1wbb6j8bzq3u0"><path d="M6 4.5v15l14-7.5-14-7.5Z" data-id="element-90" data-mp="219urb5eul9ks" /></svg>}
            </button>
          </div>

          {/* Los dos paneles están apilados en la misma celda de grid: el contenedor toma automáticamente la altura del más alto (sin medirla por JS ni provocar salto de layout al cambiar de estado), y el cambio visible es solo una transición de opacidad. */}
          <div className="grid" onPointerEnter={pauseTemporarily} onPointerLeave={scheduleResume} onPointerDown={pauseTemporarily} onPointerUp={scheduleResume} onFocus={pauseTemporarily} onBlur={scheduleResume} data-id="element-91" data-mp="37r2qcqswrze7">
            <div id={antesPanelId} role="group" aria-label={LABEL_ANTES} aria-hidden={active !== 'antes'} className={`col-start-1 row-start-1 ${prefersReducedMotionRef.current ? '' : 'transition-opacity duration-500'} ${active === 'antes' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-92" data-mp="4am25c87wn74">
              {renderList(antes, 'antes')}
            </div>

            <div id={despuesPanelId} role="group" aria-label={LABEL_DESPUES} aria-hidden={active !== 'despues'} className={`col-start-1 row-start-1 ${prefersReducedMotionRef.current ? '' : 'transition-opacity duration-500'} ${active === 'despues' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-93" data-mp="s19hgikgqf3p">
              {renderList(despues, 'despues')}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}